import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Divider, Stack, Typography } from '@mui/material';

import { ParameterMetadata, ParameterType, PrimitiveType } from '../../../../api/api';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import { ExpressionModel } from '../../Models/ExpressionModel';
import { ExpressionTypes } from '../../Models/ExpressionTypes';
import {
	StringTemplateExpressionModel,
	StringTemplateFormat,
	TEMPLATE_CONTENT_PLACEHOLDER_TOKEN,
	coerceToStringTemplate,
	extractPlaceholderIds,
	isSystemTemplatePlaceholder,
	nextPlaceholderId,
	normalizeStringTemplateFormat,
	pruneOrphanedPlaceholders,
} from '../../Models/StringTemplateModel';
import { VariableScope } from '../../Models/VariableScopes/VariableScope';
import ExpressionPlaceholder from '../Expressions/ExpressionPlaceholder';
import HtmlTemplateEditor from './HtmlTemplateEditor';

/**
 * Primitive types that make sense as template placeholder values.
 * Excludes Object, Collection, Ref, Enum, Guid, Time, TimeSpan.
 */
const TEMPLATE_PLACEHOLDER_TYPES: PrimitiveType[] = [
	PrimitiveType.String,
	PrimitiveType.Integer,
	PrimitiveType.Decimal,
	PrimitiveType.Boolean,
	PrimitiveType.Date,
	PrimitiveType.DateTime,
];

type ActivePlaceholder = { id: string; anchor?: HTMLElement } | null;

type TemplateEditorContentProps = {
	/** Current expression value from the parent dialog's pending state. */
	expression: ExpressionModel | undefined;
	/** Called whenever the template model changes so the dialog pending state stays in sync. */
	onExpressionChange: (expression: ExpressionModel) => void;
	format: StringTemplateFormat;
	parameterMetadata: ParameterMetadata;
	variableScope: VariableScope | undefined;
	previewContent?: string;
	allowPlaceholderExpressionEditing?: boolean;
	availablePlaceholderIds?: string[];
	showFormatSelector?: boolean;
	newPlaceholderLabel?: string;
	showNewPlaceholderButton?: boolean;
	showPlaceholderLegend?: boolean;
	showContentSlotButton?: boolean;
	onActivePlaceholderChange?: (id: string | undefined) => void;
};
/**
 * Self-contained template editor designed to be embedded inside ExpressionComposerDialog.
 * Manages its own content / placeholder state and calls `onExpressionChange` on every change.
 */
export default function TemplateEditorContent({
	expression,
	onExpressionChange,
	format,
	parameterMetadata,
	variableScope,
	previewContent,
	allowPlaceholderExpressionEditing = false,
	availablePlaceholderIds = [],
	showFormatSelector = true,
	newPlaceholderLabel,
	showNewPlaceholderButton = true,
	showPlaceholderLegend = true,
	showContentSlotButton = false,
	onActivePlaceholderChange,
}: TemplateEditorContentProps) {
	const placeholderParameterMetadata: ParameterMetadata = {
		...parameterMetadata,
		type: ParameterType.Expression,
		typeCustom: undefined,
		expectedType: {},
	};
	const initial = coerceToStringTemplate(expression as ExpressionModel | string | undefined, format);

	const [currentFormat, setCurrentFormat] = useState<StringTemplateFormat>(normalizeStringTemplateFormat(initial?.Format ?? format));
	const [content, setContent] = useState<string>(initial?.Content ?? '');
	const [placeholders, setPlaceholders] = useState<Record<string, ExpressionModel>>(initial?.Placeholders ?? {});
	const [active, setActive] = useState<ActivePlaceholder>(null);
	const [pendingFormat, setPendingFormat] = useState<StringTemplateFormat | undefined>();
	const lastEmittedExpressionRef = useRef<ExpressionModel | undefined>();

	// Reset when the incoming expression reference changes (dialog re-opened with different value).
	useEffect(() => {
		if (expression && expression === lastEmittedExpressionRef.current) {
			return;
		}

		const next = coerceToStringTemplate(expression as ExpressionModel | string | undefined, format);
		setCurrentFormat(normalizeStringTemplateFormat(next?.Format ?? format));
		setContent(next?.Content ?? '');
		setPlaceholders(next?.Placeholders ?? {});
		setActive(null);
		setPendingFormat(undefined);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [expression]);

	/** Emit a fresh StringTemplateModel whenever content or placeholders change. */
	const emit = useCallback(
		(newContent: string, newPlaceholders: Record<string, ExpressionModel>, newFormat: StringTemplateFormat = currentFormat) => {
			const nextExpression = {
				Type: ExpressionTypes.StringTemplate,
				Format: newFormat,
				Content: newContent,
				Placeholders: pruneOrphanedPlaceholders(newContent, newPlaceholders),
			} as StringTemplateExpressionModel;

			lastEmittedExpressionRef.current = nextExpression;
			onExpressionChange(nextExpression);
		},
		[currentFormat, onExpressionChange],
	);

	const applyFormatChange = useCallback(
		(value: StringTemplateFormat) => {
			if (value === currentFormat) {
				return;
			}

			let nextContent = content;
			if (value !== 'Html' && currentFormat === 'Html') {
				// Convert HTML to plain text: <p> becomes \n, all other tags stripped.
				const doc = new DOMParser().parseFromString(content, 'text/html');
				doc.querySelectorAll('p').forEach((p) => {
					const frag = doc.createDocumentFragment();
					frag.append(...Array.from(p.childNodes));
					frag.appendChild(doc.createTextNode('\n'));
					p.replaceWith(frag);
				});
				doc.querySelectorAll('br').forEach((br) => br.replaceWith(doc.createTextNode('\n')));
				nextContent = (doc.body.textContent ?? '').replace(/\n$/, '');
			} else if (value === 'Html' && currentFormat !== 'Html') {
				// Convert plain text or Markdown to HTML: \n becomes <p> paragraph boundaries.
				nextContent = content
					.split('\n')
					.map((line) => `<p>${line}</p>`)
					.join('');
			}
			setContent(nextContent);
			setCurrentFormat(value);
			emit(nextContent, placeholders, value);
		},
		[content, currentFormat, placeholders, emit],
	);

	const handleFormatChange = useCallback(
		(value: StringTemplateFormat) => {
			if (value === currentFormat) {
				return;
			}

			setPendingFormat(value);
		},
		[currentFormat],
	);

	const handleContentChange = useCallback(
		(newContent: string) => {
			setContent(newContent);
			emit(newContent, placeholders);
		},
		[placeholders, emit],
	);

	const handleInsertPlaceholder = useCallback(
		(anchorEl: HTMLElement, insertAt?: (token: string) => void) => {
			const id = nextPlaceholderId(placeholders);
			const token = `{{${id}}}`;
			const emptyExpr: ExpressionModel = { Type: 'Const', Value: '' };
			const newPlaceholders = { ...placeholders, [id]: emptyExpr };
			setPlaceholders(newPlaceholders);
			if (insertAt) {
				insertAt(token);
			} else {
				const newContent = content + token;
				setContent(newContent);
				emit(newContent, newPlaceholders);
			}
			setActive({ id, anchor: anchorEl });
		},
		[content, placeholders, emit],
	);

	const handleInsertTemplateContentPlaceholder = useCallback(
		(insertAt?: (token: string) => void) => {
			if (insertAt) {
				insertAt(TEMPLATE_CONTENT_PLACEHOLDER_TOKEN);
				return;
			}

			const newContent = content + TEMPLATE_CONTENT_PLACEHOLDER_TOKEN;
			setContent(newContent);
			emit(newContent, placeholders);
		},
		[content, placeholders, emit],
	);

	const handlePlaceholderExpressionChange = useCallback(
		(id: string, expr: ExpressionModel | undefined) => {
			setPlaceholders((prev) => {
				const next = { ...prev };
				if (!expr) {
					delete next[id];
				} else {
					next[id] = expr;
				}
				emit(content, next);
				return next;
			});
		},
		[content, emit],
	);

	const placeholderIds = useMemo(() => extractPlaceholderIds(content), [content]);
	const configurablePlaceholderIds = useMemo(() => placeholderIds.filter((id) => !isSystemTemplatePlaceholder(id)), [placeholderIds]);

	return (
		<Stack direction="column" sx={{ height: '100%', overflow: 'hidden', gap: 1 }}>
			<ConfirmDialog
				open={pendingFormat !== undefined}
				title="Change Template Format"
				message="Changing the template format can erase formatting. Do you want to continue?"
				confirmText="Change Format"
				confirmColor="warning"
				onConfirm={() => {
					if (pendingFormat) {
						applyFormatChange(pendingFormat);
					}
					setPendingFormat(undefined);
				}}
				onCancel={() => setPendingFormat(undefined)}
			/>

			{/* Editor */}
			<Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
				<HtmlTemplateEditor
					content={content}
					previewContent={previewContent}
					placeholders={placeholders}
					onContentChange={handleContentChange}
					onInsertPlaceholder={handleInsertPlaceholder}
					onInsertTemplateContentPlaceholder={handleInsertTemplateContentPlaceholder}
					onPlaceholderClick={(id, anchor) => {
						if (!isSystemTemplatePlaceholder(id)) {
							setActive({ id, anchor });
						}
					}}
					variableScope={variableScope}
					showFormatting={currentFormat === 'Html'}
					activeId={active?.id}
					format={currentFormat}
					onFormatChange={handleFormatChange}
					availablePlaceholderIds={availablePlaceholderIds}
					showFormatSelector={showFormatSelector}
					newPlaceholderLabel={newPlaceholderLabel}
					showNewPlaceholderButton={showNewPlaceholderButton}
					showPlaceholderLegend={showPlaceholderLegend}
					showContentSlotButton={showContentSlotButton}
					onActivePlaceholderChange={(id) => {
						setActive(id ? { id } : null);
						onActivePlaceholderChange?.(id);
					}}
				/>
			</Box>
			{placeholderIds.length === 0 && (
				<Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, fontStyle: 'italic' }}>
					{showNewPlaceholderButton ? 'No placeholders yet - click "Insert Placeholder" to add one.' : 'No placeholders in this template yet.'}
				</Typography>
			)}
			{allowPlaceholderExpressionEditing && (
				<>
					<Divider />

					<Box sx={{ flexShrink: 0, minHeight: 80 }}>
						{active ? (
							<Stack direction="column" spacing={1}>
								<ExpressionPlaceholder
									key={active.id}
									expression={placeholders[active.id]}
									onExpressionChange={(expr) => handlePlaceholderExpressionChange(active.id, expr)}
									parameterMetadata={placeholderParameterMetadata}
									variableScope={variableScope}
									isEditable={true}
									allowedResultTypes={TEMPLATE_PLACEHOLDER_TYPES}
								/>
							</Stack>
						) : (
							<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
								{placeholderIds.length === 0
									? 'No placeholders yet - click "Insert Placeholder" to add one.'
									: configurablePlaceholderIds.length === 0
										? 'Only system placeholders are present.'
										: 'Click a placeholder in the editor to configure its expression.'}
							</Typography>
						)}
					</Box>
				</>
			)}
		</Stack>
	);
}
